import Skeleton from '@material-ui/lab/Skeleton';
import { Dictionary } from 'lodash';
import { groupBy, sortBy } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IReport } from '~/src/dash/api/console-api';
import {
  CollapsibleCheckboxes,
  EDisplayExpandOrCollapseAll,
  handleClickCollapseAll,
  handleClickExpandAll,
} from '~/src/dash/components/CollapsibleCheckboxes';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { spacingV2 } from '~/src/dash/components/layout/common';
import { getLineHeight } from '~/src/dash/components/text/common';
import { TextWithTooltip } from '~/src/dash/components/text/TextWithTooltip';
import {
  borderRadius,
  Col,
  Cols,
  Row,
  Rows,
  Text,
  TextButton,
  TextInput,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { REPORTING_MAX_SELECTED_CHARS } from '~/src/dash/views/reporting/ReportBuilder/common';

export interface IDimensionsSectionProps {
  dimensions: IDimension[];
  selectedDimensionKeys: string[];
  setSelectedDimensionKeys: (ids: string[]) => void;
  isLoading: boolean;
  report: IReport | null;
}

interface IDimensionSection {
  dimensions: IDimension[];
  isSelectAllDisabled: boolean;
  label: string;
}

interface IDimension {
  title: string;
  ID: string;
  subheader: string;
  selected: boolean;
  disabled: boolean;
}

export const DimensionsSection = ({
  dimensions,
  selectedDimensionKeys,
  setSelectedDimensionKeys,
  isLoading,
  report,
}: IDimensionsSectionProps) => {
  const [search, setSearch] = useState<string>('');
  const [displayExpandOrCollapseAll, setDisplayExpandOrCollapseAll] = useState(
    EDisplayExpandOrCollapseAll.Collapse,
  );
  const [isExpandAll, setIsExpandAll] = useState<boolean>(true);
  const [isCollapseAll, setIsCollapseAll] = useState<boolean>(false);

  useEffect(() => {
    if (search.length > 0) {
      setIsExpandAll(true);
    }
  }, [search]);

  useEffect(() => {
    unstable_batchedUpdates(() => {
      if (report) {
        setDisplayExpandOrCollapseAll(EDisplayExpandOrCollapseAll.Expand);
        setIsExpandAll(false);
        setIsCollapseAll(true);
      }
    });
  }, [report]);

  const handleClickCheckbox = (id: string): void => {
    // do nothing if the dimension is disabled
    if (dimensions.find((dimension) => dimension.ID === id)?.disabled) return;

    // otherwise select or deselect the dimension accordingly
    const newSelectedDimensionKeys = [...selectedDimensionKeys];
    if (newSelectedDimensionKeys.includes(id)) {
      newSelectedDimensionKeys.splice(newSelectedDimensionKeys.indexOf(id), 1);
    } else {
      newSelectedDimensionKeys.push(id);
    }

    setSelectedDimensionKeys(newSelectedDimensionKeys);
  };

  const onExpandAll = () => {
    unstable_batchedUpdates(() => {
      handleClickExpandAll(setIsExpandAll, setIsCollapseAll);
      setDisplayExpandOrCollapseAll(EDisplayExpandOrCollapseAll.Collapse);
    });
  };

  const onCollapseAll = () => {
    unstable_batchedUpdates(() => {
      handleClickCollapseAll(setIsExpandAll, setIsCollapseAll);
      setDisplayExpandOrCollapseAll(EDisplayExpandOrCollapseAll.Expand);
    });
  };

  const handleClickCheckboxColumn = (ids: string[], selected: boolean) => {
    const newSelectedDimensionKeys = [...selectedDimensionKeys];
    ids.forEach((id) => {
      if (selected && !newSelectedDimensionKeys.includes(id)) {
        newSelectedDimensionKeys.push(id);
      } else if (!selected && newSelectedDimensionKeys.includes(id)) {
        newSelectedDimensionKeys.splice(newSelectedDimensionKeys.indexOf(id), 1);
      }
    });
    setSelectedDimensionKeys(newSelectedDimensionKeys);
  };

  const handleClearDimensions = () => {
    setSelectedDimensionKeys([]);
  };

  const sortDimensionsSectionsIntoCols = (
    dimensionSections: Dictionary<IDimension[]>,
  ): IDimensionSection[][] => {
    const cols = [[], [], [], []] as IDimensionSection[][];

    const FIRST_COLUMN_DIMENSIONS = ['Time Range', 'Demand', 'Line Item / Order'];
    const SECOND_COLUMN_DIMENSIONS = ['Supply', 'Geo', 'Device'];
    const THIRD_COLUMN_DIMENSIONS = ['Content', 'Routes', 'Privacy', 'Inventory Route'];
    const FOURTH_COLUMN_DIMENSIONS = ['Custom', 'Others', 'Preset Aggregate'];
    const ALL_DIMENSIONS = [
      FIRST_COLUMN_DIMENSIONS,
      SECOND_COLUMN_DIMENSIONS,
      THIRD_COLUMN_DIMENSIONS,
      FOURTH_COLUMN_DIMENSIONS,
    ];

    ALL_DIMENSIONS.forEach((dimensionCol, idx) => {
      dimensionCol.forEach((dimensionSectionLabel) => {
        const dimensionSection = dimensionSections[dimensionSectionLabel];
        if (dimensionSection) {
          cols[idx].push({
            dimensions:
              dimensionSectionLabel === 'Custom'
                ? // special sort for dimensions under 'Custom'
                  sortBy(
                    sortBy(dimensionSection, 'title', 'asc'),
                    ({ title }, idx) => {
                      const suffix = Number(title.split('Custom')[1]);
                      // the highest is Custom18 so this will put all other Custom fields after the numbered ones
                      return !isNaN(suffix) ? suffix : 19 + idx;
                    },
                    'asc',
                  )
                : // default string sort for all others
                  sortBy(dimensionSection, 'title', 'asc'),
            isSelectAllDisabled:
              dimensionSectionLabel === 'Time Range' ||
              dimensionSectionLabel === 'Preset Aggregate',
            label: dimensionSectionLabel,
          });
        }
      });
    });

    return cols;
  };

  const createDimensionSections = (dimensions: IDimension[]): IDimensionSection[][] => {
    const dimensionsGrouped = groupBy(dimensions, 'subheader');
    return sortDimensionsSectionsIntoCols(dimensionsGrouped);
  };

  const getFilteredDimensions = (): IDimension[] => {
    const query = search.toLowerCase();
    return search.length > 0
      ? dimensions.filter((dimension) => {
          const title = dimension.title.toLowerCase();
          const subheader = dimension.subheader.toLowerCase();
          return title.includes(query) || subheader.includes(query);
        })
      : dimensions;
  };

  const filteredDimensions = getFilteredDimensions();
  const dimensionSections = createDimensionSections(filteredDimensions);
  const selectedDimensions = dimensions.filter((dimension) => dimension.selected);
  const numSelectedDimensions = selectedDimensions.length;
  const selectedDimensionNames = selectedDimensions.map((dimension) => dimension.title).join(', ');

  return (
    <Rows>
      <Row>
        <Cols gap={spacingV2.medium}>
          <Col displayFlex style={{ alignItems: 'center' }}>
            <IconV2 name='vector' />
          </Col>
          <Col displayFlex extraSmallVerticalPaddingV2 style={{ alignItems: 'center' }}>
            <Text sizeV2 bold large>
              Dimensions
            </Text>
          </Col>
          <Col displayFlex style={{ alignItems: 'center' }}>
            <Text muted extraSmall>
              (Group By)
            </Text>
          </Col>
          {numSelectedDimensions > 0 && (
            <>
              <Col displayFlex style={{ alignItems: 'center', paddingLeft: spacingV2.extraLarge }}>
                <Text small bold>{`${numSelectedDimensions} selected`}</Text>
              </Col>
              <Col displayFlex style={{ alignItems: 'center' }}>
                <TextWithTooltip
                  small
                  tooltip={`${selectedDimensionNames}`}
                  style={{
                    borderRadius: borderRadius.base,
                    backgroundColor: publicaColors.veryLightBlue,
                    padding: `${spacingV2.extraSmall}`,
                  }}
                >
                  {selectedDimensionNames.length < REPORTING_MAX_SELECTED_CHARS
                    ? `${selectedDimensionNames}`
                    : `${selectedDimensionNames.slice(0, REPORTING_MAX_SELECTED_CHARS)}...`}
                </TextWithTooltip>
              </Col>
              <Col displayFlex style={{ alignItems: 'center' }}>
                <TextButton size='small' weight='none' onClick={handleClearDimensions}>
                  Clear
                </TextButton>
              </Col>
            </>
          )}
        </Cols>
      </Row>

      <Rows extraLargeHorizontalPaddingV2>
        <Row extraSmallVerticalPaddingV2>
          <Cols gap={spacingV2.large} style={{ display: 'flex', alignItems: 'center' }}>
            <Col>
              <TextInput
                enableClear
                handleOnChange={(input: string) => setSearch(input)}
                inputSize='small'
                msDelay={500}
                placeholder={`Search`}
                prefix='search'
              />
            </Col>
            {displayExpandOrCollapseAll === EDisplayExpandOrCollapseAll.Expand && (
              <Col>
                <TextButton
                  weight='none'
                  size='small'
                  onClick={onExpandAll}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <IconV2 name='expand' color={publicaColors.blue} />
                  Expand All
                </TextButton>
              </Col>
            )}
            {displayExpandOrCollapseAll === EDisplayExpandOrCollapseAll.Collapse && (
              <Col displayFlex style={{ alignItems: 'center' }}>
                <TextButton
                  weight='none'
                  size='small'
                  onClick={onCollapseAll}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <IconV2 name='collapse' color={publicaColors.blue} />
                  Collapse All
                </TextButton>
              </Col>
            )}
          </Cols>
        </Row>
        <Row>
          <Cols wrap>
            {dimensionSections.map((section) => {
              return !isLoading ? (
                <Col
                  style={{
                    width: section.length > 0 ? `calc(100% / ${dimensionSections.length})` : 0,
                    minWidth: section.length > 0 ? '10rem' : 0,
                  }}
                >
                  {section.map((dimensionSection) => {
                    return (
                      <CollapsibleCheckboxes
                        title={dimensionSection.label}
                        list={dimensionSection.dimensions}
                        onChange={handleClickCheckbox}
                        onSectionChange={handleClickCheckboxColumn}
                        isExpandAll={isExpandAll}
                        setIsExpandAll={setIsExpandAll}
                        isCollapseAll={isCollapseAll}
                        setIsCollapseAll={setIsCollapseAll}
                        isDisabled={dimensionSection.isSelectAllDisabled}
                      />
                    );
                  })}
                </Col>
              ) : (
                <Skeleton
                  variant='text'
                  animation='wave'
                  width={`calc(100% / ${dimensionSections.length} - ${spacingV2.large})`}
                  height={getLineHeight('extraLarge')}
                  style={{ marginRight: spacingV2.large }}
                />
              );
            })}
          </Cols>
        </Row>
      </Rows>
    </Rows>
  );
};
